import { FC, useEffect, useMemo } from "react"
import { useQueryClient } from "react-query"

import { Formik, Form, FormikValues, FieldArray} from 'formik'
import * as Yup from 'yup'

import { KTSVG } from "../../../../../../../_metronic/helpers"

import { submitNewDLMApplication, submitDLMPhase_1_2, editDLMApplication, SCDFPlanNoSchema, DLMApplication, LeafDoorType } from "../../../../../core/doclabel"
import { array_fill_keys, doFormSubmit, isPopulatedArray, processNoteValues } from "../../../../../../components/helpers"
import {
  StatusErrorMessage,
  FormikTextInput, FormikTextField, FormikFileUpload,
  FileUploaderResponse,
  FormikAcknowledgeFeeCheck,
  FormikNotesArea
} from "../../../../../../components/widgets"

import { AttachmentCard } from "../../../../../core/attachments/AttachmentCard"

import { cloneDeep, pickBy, filter, pick } from "lodash"

import { DLMROIEditDetailRecord, IDLMROIEditFormModel, IDLMROIEditItemModel } from "./_models"
import { SubmitButtonRow } from "../../../../../../components/layout"
import { DLMROIEditModalDetailsTable } from "./DLMROIEditModalDetailsTable"
import clsx from "clsx"


const roiEditDetailItemSchema: any = Yup.object({
  id: Yup.number().integer().label('Detail Id'),

  itemNo: Yup.string().trim(),
  labelFrom: Yup.string().label('Label From'),
  labelTo: Yup.string().label('Label To'),
  quantity: Yup.number().integer().moreThan(0, 'Must be positive').required().label('Quantity')
  .max(Yup.ref('originalQty'), ({max}) => `Exceeds original quantity of ${max}.`),
  size: Yup.string().trim().required().label('Size'),
  remarks: Yup.string().trim().label('Remarks'),

  hourRating: Yup.string().label('hourRating'),
  targetDetailId: Yup.number().integer().label('Target ROI Detail Id'),
  originalQty: Yup.number().integer().label('Original Quantity'),
})

const roiEditDetailsSchema: Yup.ObjectSchema<DLMROIEditDetailRecord> = Yup.lazy((obj) => {
  if (!obj) return Yup.object();

  const shape = Object.keys(obj).reduce((acc, key) => {
    acc[key] = Yup.array().of(roiEditDetailItemSchema);
    return acc;
  }, {} as any);

  return Yup.object().shape(shape);
}) as any;

const roiEditSchema: any = Yup.object({
  projectName: Yup.string().trim().label('Project Name').required(),
  installerName: Yup.string().trim().label('Installer Company'),
  bpNumber: SCDFPlanNoSchema,
  clientRefNo: Yup.string().trim().label('Client Ref No'),
  otherReference: Yup.string().trim().label('Other Reference No'),
  details: roiEditDetailsSchema,
  otherAttachments: Yup.array(),
  comment: Yup.string().trim().label('Reason for Editing').required(),
  note: Yup.string().trim().label("Notes"),
  noteAttachments: Yup.array(),
  acknowledgeFee: Yup.bool().oneOf([true], 'Must be checked').label('Acknowledge Fee'),
});


const initROIEditFormValues: IDLMROIEditFormModel = {
  projectName: '',
  installerName: '',
  bpNumber:  '',
  clientRefNo:  '',
  otherReference: '',
  details: {},
  otherAttachments: [],
  comment: '',
  note: '',
  noteAttachments: [],
  acknowledgeFee: false,
  isDoor: false,
}

const ROIEDIT_DATA_FIELDS = [
  'id',
  'projectName',
  'installerName',
  'bpNumber',
  'clientRefNo',
  'otherReference',
  'otherAttachments',
  'comment'
];

const ROIEDIT_ITEM_FIELDS = [
  'itemNo',
  'labelFrom',
  'labelTo',
  'quantity',
  'hourRating',
  'size',
  'remarks',
  'doorType',
  'visionPanel',
  // not in original
  'targetDetailId',
];

type Props = {
  appData: DLMApplication
  doClose: Function
  onSuccess: Function
  appId?: number
  resubmitMode?: string
}

/**
 * Extracts sizeF and sizeL from a size string formatted like "3x3(F) 4x4(L)"
 */
const parseSizes = (sizeStr: string = ""): { sizeF: string; sizeL: string } => {
  const fMatch = sizeStr.match(/([^\s()]+)\(F\)/i);
  const lMatch = sizeStr.match(/([^\s()]+)\(L\)/i);

  return {
    sizeF: fMatch ? fMatch[1] : "",
    sizeL: lMatch ? lMatch[1] : "",
  };
};


const DLMROIEditModal:FC<Props> = ({appData, doClose, onSuccess, appId, resubmitMode = ''}) => {

  const queryClient = useQueryClient();

  const { initValues, cbRef, targetROI } = useMemo(() => {

    if (! appData) {
      return { initValues: {...initROIEditFormValues}, cbRef: '', targetROI: 0};
    }

    const cbRef = appData?.targetROI ? appData.targetROI.cbRef: '';
    const targetROI = appData?.targetROI ? appData.targetROI.id: 0;
    const isDoor =  appData?.targetROI?.dlmType?.door ?? false;

    let _init = {
      ...initROIEditFormValues,
      ...pick(appData,['projectName','comment']),
      isDoor,
    }

    if (appData?.otherAttachments && isPopulatedArray(appData.otherAttachments))  {
      Object.assign(_init, {otherAttachments: appData.otherAttachments.map(o => { return { id: o.id, name: o.fileName}} )});
    }

    if (! (appData?.siteApplications && isPopulatedArray(appData.siteApplications))) {
      return { initValues: _init, cbRef, targetROI};
    }

    const _site = appData.siteApplications[0];

    Object.assign(_init, pick(_site, ROIEDIT_DATA_FIELDS));

    if (_site?.siteApplicationDetails && isPopulatedArray(_site.siteApplicationDetails)) {
      Object.assign(_init, {
        details : filter(_site.siteApplicationDetails, o => (! o?.deleted)).reduce(
        (accum, curr) => {
          let newobj = {
            ...array_fill_keys(['id','targetDetailId','originalQty'], 0),
            ...pick(curr,ROIEDIT_ITEM_FIELDS),
            ...(isDoor ? parseSizes(curr.size) : {}),            
          }
          let targetDetailId = 0;

          // retrieve from current application
          if (curr?.targetDetail && curr.targetDetail?.id) {
            targetDetailId = curr.targetDetail.id;
            Object.assign(newobj, {
              targetDetailId: curr.targetDetail.id,
              originalQty: curr.targetDetail.quantity,
            })
          }

          if (! accum.hasOwnProperty(`${targetDetailId}`)) {
            Object.assign(accum, { [`${targetDetailId}`] : [newobj]});
            return accum;
          }
          // @ts-ignore
          let _itemset = cloneDeep(accum[`${targetDetailId}`]);
          _itemset.push(newobj)

          Object.assign(accum, {[`${targetDetailId}`] : _itemset });
          return accum;
        },{})
      });
    }

    return { initValues: _init, cbRef, targetROI};

  }, [appData]);

  const { isDoor } = initValues;

  const submitFunc = useMemo(() => {
    switch(resubmitMode) {
    case 'resubmit':
      return submitDLMPhase_1_2;
    case 'edit':
      return editDLMApplication;
    default:
      return submitNewDLMApplication;
    }
  }, [resubmitMode]);

  const doSubmit = async (values: any, actions: FormikValues) => {

    const { setStatus, setSubmitting } = actions;

    // extract item rows from details
    let dlmWebPhase0DetailsModels = [];
    for (const items of Object.values(values.details)) {
      dlmWebPhase0DetailsModels.push(...(items as Array<IDLMROIEditItemModel>).map( 
        (item: any) => pickBy(item, (value, key) => (
          (value && ROIEDIT_ITEM_FIELDS.includes(key)) || 
          (key === 'visionPanel'))
        )));
    }

    const _site = {
      ...pickBy(values, (value, key) => (value && ROIEDIT_DATA_FIELDS.includes(key))),
      dlmWebPhase0DetailsModels
    }

    let _payload = {
      ...pickBy(values, (value, key) => value && ['projectName','installerName','comment'].includes(key)),
      targetROI,
      applicationType: "TYPE_ROIEDIT",
      dlmWebPhase0SitesModel: [_site]
    };

    if (values.hasOwnProperty('otherAttachments') && values.otherAttachments.length > 0) {
      Object.assign(_payload, {otherAttachments: values.otherAttachments.map((o: any) => (o.id))});
    }

    if (appId) {
      Object.assign(_payload, { id: appId});
    }
    
    if (resubmitMode) {
      //@ts-ignore
      _payload =  { application: _payload };      
      processNoteValues(values, _payload);
    }

    await doFormSubmit(submitFunc, _payload, setStatus, setSubmitting, (res) => {      
      queryClient.invalidateQueries(['DLMAPPLICATIONS-LIST', { lastpath: 'label-applications-list' } ]);
      queryClient.invalidateQueries(['DLMAPPLICATIONS-LIST', { listmode: 'own'} ]);
      if (appId) {
        queryClient.invalidateQueries(['DLMAPPLICATION-LOAD', { dlmappid: appId } ]);
      }
      if (res?.data) {
        onSuccess(res.data);
      }      
    });
  }

  useEffect(() => {
    document.body.classList.add('modal-open')
    return () => {
      document.body.classList.remove('modal-open')
    }
  }, [])

  return (
  <>
  <div
    className='modal fade show d-block'
    id='kt_modal_roi_edit'
    role='dialog'
    tabIndex={-1}
    aria-modal='true'
  >
    <div className={clsx('modal-dialog modal-dialog-centered',{'mw-800px' : ! isDoor},{'mw-1000px' : isDoor})}>
      <div className='modal-content'>
        <div className='modal-header'>
          <h2 className='fw-bolder'>Edit ROI : {cbRef}</h2>
          <div
            className='btn btn-icon btn-sm btn-active-icon-primary'
            data-kt-applicants-modal-action='close'
            onClick={()=> { doClose()}}
            style={{cursor: 'pointer'}}
          >
            <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-1' />
          </div>
        </div>
        <div className='modal-body px-8 py-6'>
          <Formik
            initialValues={initValues}
            validationSchema={roiEditSchema}
            enableReinitialize={true}
            onSubmit={doSubmit}
          >
          {({status, values}) => (
            <Form id='kt_roiedit_form' className='form' noValidate>
              <div className="row mb-2">
                <FormikTextField size="12" label="Project Name" name="projectName" required rows={2} />
              </div>
              <div className="row mb-2">
                <FormikTextField size="12" label="Installer Company" name="installerName" rows={1} />
              </div>
              <div className="row mb-2">
                <FormikTextInput size="12 col-md-4" label="SCDF Building Plan No" name="bpNumber" />
                <FormikTextInput size="12 col-md-4" label="Client Reference No" name="clientRefNo" />
                <FormikTextInput size="12 col-md-4" label="Other Reference No" name="otherReference" />
              </div>
              <div className="mb-2 row">
                <FormikFileUpload size="6 mt-5" label="Other Attachments" name="otherAttachments" acceptFiles="ApplicantFileFormats" isFileArray={true} />
              </div>
              <FieldArray
                name="otherAttachments"
                render={arrayHelpers => (
                <div className="d-flex flex-row flex-wrap gap-2 mh-80px overflow-auto">
                  { (values.otherAttachments && values.otherAttachments.length > 0) &&
                  values.otherAttachments.map( (o: FileUploaderResponse, idx: number) => (
                  <AttachmentCard
                    key={`otherAttachment${idx}`}
                    className="border py-1"
                    idx={idx}
                    fileId={o.id}
                    fileName={o.name}
                    onRemove={(e: any) => {
                      e.stopPropagation();
                      const elem = e.target.closest('.attachment-card');
                      if (elem) {
                        const {idx} = elem.dataset;
                        arrayHelpers.remove(idx);
                      }
                    }}
                  />
                  ))
                  }
                </div>
                )}
              />

              <DLMROIEditModalDetailsTable />
              <div className='separator border-gray-200 my-0'></div>
              <div className="row mb-2 mt-4">
                <FormikTextField size="12" label="Reason for Editing" name="comment" required={true} rows={2} />
              </div>
              { resubmitMode &&
              <>
              <div className='separator border-gray-200 my-4'></div>
              <FormikNotesArea />
              </>
              }

              <FormikAcknowledgeFeeCheck dlmtype="ROI"/>

              <StatusErrorMessage status={status} />
              <SubmitButtonRow doClose={() => doClose()} showDebug />
            </Form>
          )}
          </Formik>
        </div>
      </div>
    </div>
  </div>
  <div className='modal-backdrop fade show'></div>
  </>
  )
}

export {DLMROIEditModal}